/**
 * Removes a leading speaker label from a transcript quote so the speaker isn't
 * duplicated when the quote is shown next to a dedicated "Responsible" column.
 *
 * Strips a prefix that either matches the provided responsible-party name
 * (e.g. "John Smith: …") or the generic "Speaker N:" pattern. Supports both the
 * ASCII ":" and the full-width "：" colon commonly used in CJK text.
 */
export const stripSpeakerPrefix = (
  text: string | undefined | null,
  speaker?: string | null,
): string => {
  if (!text) return '';
  let result = text.replace(/^\s+/, '');

  // Strip a prefix matching the known responsible party (handles real names too).
  // `speaker` may be a comma-separated list when several parties are responsible
  // (exports join their names), so try the full string first — which preserves
  // "Last, First" names — then each individual name, so the single leading
  // "<name>:" label in the quote is stripped regardless of how many are listed.
  const name = speaker?.trim();
  if (name) {
    const candidates = [name, ...name.split(',').map((n) => n.trim())].filter(Boolean);
    for (const candidate of candidates) {
      const escaped = candidate.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      result = result.replace(new RegExp(`^${escaped}\\s*[:：]\\s*`, 'i'), '');
    }
  }

  // Strip a generic "Speaker N:" prefix regardless of the responsible value.
  result = result.replace(/^Speaker\s+\d+\s*[:：]\s*/i, '');

  return result.replace(/^\s+/, '');
};

/**
 * Detects full-width characters (CJK ideographs, Kana, Hangul, full-width
 * punctuation) that occupy roughly twice the horizontal space of a Latin
 * character. Useful for layout math — e.g. estimating wrapped-row height in
 * exports — where wide scripts would otherwise be under-counted.
 */
export const isWideChar = (char: string): boolean => {
  const code = char.codePointAt(0) ?? 0;
  return (
    (code >= 0x1100 && code <= 0x115f) || // Hangul Jamo
    (code >= 0x2e80 && code <= 0x303e) || // CJK Radicals / Kangxi / punctuation
    (code >= 0x3041 && code <= 0x33ff) || // Hiragana, Katakana, CJK symbols
    (code >= 0x3400 && code <= 0x4dbf) || // CJK Extension A
    (code >= 0x4e00 && code <= 0x9fff) || // CJK Unified Ideographs
    (code >= 0xac00 && code <= 0xd7a3) || // Hangul Syllables
    (code >= 0xf900 && code <= 0xfaff) || // CJK Compatibility Ideographs
    (code >= 0xfe30 && code <= 0xfe4f) || // CJK Compatibility Forms
    (code >= 0xff00 && code <= 0xff60) || // Full-width forms
    (code >= 0xffe0 && code <= 0xffe6) // Full-width signs
  );
};

// Section-level headers that are not discussion topics in their own right.
const NON_TOPIC_HEADER = /^(summary|key\s+discussion\s+points|action\s+items?|agreements?)$/i;

/**
 * Capitalizes the first word of a discussion topic while preserving any leading
 * "N." / "N)" numbering. Backend summaries frequently emit topics in lowercase
 * (e.g. "1. version check framework"); this normalises them to
 * "1. Version check framework" so exported topic headings read as proper titles.
 *
 * Only the first letter is touched (sentence case), so multi-word topics keep
 * their original wording. Non-cased scripts (e.g. CJK) are returned unchanged.
 */
export const capitalizeTopicHeader = (header: string): string =>
  header.replace(
    /^(\s*(?:\d+[.)]\s*)?)(\p{L})/u,
    (_match, prefix: string, firstLetter: string) => prefix + firstLetter.toLocaleUpperCase(),
  );

/**
 * Extracts structured discussion points with headers from a markdown summary.
 *
 * Real summaries render the "Key Discussion Points" breakdown as numbered
 * sub-headers, but the exact heading level the model emits varies between
 * `### 1. Topic` and `#### 1. Topic` (and occasionally deeper). We therefore
 * match any sub-header of three-to-six `#` and skip the section-level headers
 * ("Summary", "Key Discussion Points", …). Bullets may be authored with `*`,
 * `-`, or an already-normalised `•` marker. Topic headings are sentence-cased
 * via {@link capitalizeTopicHeader} so lowercase source topics still read well.
 *
 * Returns an array with the header and bullet points for each topic section.
 */
export const extractDiscussionStructure = (
  text: string | undefined | null,
): Array<{ header: string; points: string[] }> => {
  if (!text) return [];

  // Match numbered/topic sub-headers at any level of 3-6 `#` (### or #### …).
  const headerRegex = /^#{3,6}[ \t]+(.+?)\s*$/gm;
  let match: RegExpExecArray | null;
  const matches: Array<{ header: string; startIndex: number }> = [];

  while ((match = headerRegex.exec(text)) !== null) {
    const header = match[1].trim();
    // Ignore section headers (e.g. a "### Key Discussion Points") so only real
    // discussion topics become sections.
    if (NON_TOPIC_HEADER.test(header)) continue;
    matches.push({ header, startIndex: match.index });
  }

  const sections: Array<{ header: string; points: string[] }> = [];

  // Extract bullet content between consecutive topic headers.
  for (let i = 0; i < matches.length; i++) {
    const currentMatch = matches[i];
    const nextIndex = matches[i + 1]?.startIndex ?? text.length;
    const sectionContent = text.substring(currentMatch.startIndex, nextIndex);

    const points: string[] = [];
    sectionContent
      .split('\n')
      .slice(1) // Skip the header line itself
      .forEach((line) => {
        // Match markdown bullets (`*`, `-`) or normalised bullets (`•`).
        const bulletMatch = line.trim().match(/^[*\-•]\s+(.+)$/);
        if (bulletMatch) {
          points.push(bulletMatch[1].trim());
        }
      });

    sections.push({ header: capitalizeTopicHeader(currentMatch.header), points });
  }

  return sections;
};

/**
 * Cleans summary text by removing markdown metadata and formatting.
 * Used by PDF, DOCX, and Excel exports.
 */
export const cleanSummaryText = (text: string | undefined | null): string => {
  if (!text) return '';
  return text
    .replace(/\*\*Start:\*\*[^\n]*\n?/gi, '')
    .replace(/\*\*End:\*\*[^\n]*\n?/gi, '')
    .replace(/\*\*Location:\*\*[^\n]*\n?/gi, '')
    .replace(/^##\s*Summary\s*\n?/gim, '') // Remove ## Summary header
    .replace(/^##\s*Key\s+Discussion\s+Points\s*\n?/gim, '') // Remove ## Key Discussion Points header
    .replace(/^#{3,}\s+.*$/gm, '') // Remove ### and higher headers (subsections)
    .replace(/^\*\s+/gm, '• ') // Convert markdown bullets to bullet points
    .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold markers but keep text
    .replace(/\*(.+?)\*/g, '$1') // Remove italic markers but keep text
    .replace(/\*\*/g, '') // Remove any remaining bold markers
    .replace(/\n{3,}/g, '\n\n') // Collapse multiple newlines
    .trim();
};

/**
 * Returns just the introductory prose of a summary — the text that appears
 * before the "Key Discussion Points" breakdown (or, failing that, before the
 * first numbered topic sub-header).
 *
 * Exports render this intro on its own and then render the structured topics via
 * {@link extractDiscussionStructure}; this prevents the topic bullets from also
 * appearing as a flattened list. When a summary has no structured breakdown the
 * full cleaned text is returned, so unstructured summaries still render in full.
 */
export const extractSummaryIntro = (text: string | undefined | null): string => {
  if (!text) return '';

  const keyPointsHeader = text.match(/^#{2,6}\s*Key\s+Discussion\s+Points\b.*$/im);
  const firstTopicHeader = text.match(/^#{3,6}[ \t]+.+$/m);

  const cutPoints = [keyPointsHeader?.index, firstTopicHeader?.index].filter(
    (index): index is number => typeof index === 'number',
  );

  const cutIndex = cutPoints.length > 0 ? Math.min(...cutPoints) : -1;
  const intro = cutIndex >= 0 ? text.slice(0, cutIndex) : text;

  return cleanSummaryText(intro);
};
export const parseSummaryIntoParts = (
  text: string | undefined | null,
): { mainText: string; bulletPoints: string[] } => {
  if (!text) return { mainText: '', bulletPoints: [] };

  const cleaned = cleanSummaryText(text);

  // Split by bullet points
  const parts = cleaned.split(/\s*•\s*/);

  // First part is the main text (before any bullets)
  const mainText = parts[0]?.trim() || '';

  // Rest are bullet points
  const bulletPoints = parts
    .slice(1)
    .map((p) => p.trim())
    .filter(Boolean);

  return { mainText, bulletPoints };
};

/**
 * The key discussion points are edited as plain text: a line is a topic, unless
 * it starts with `-`, `*` or `•` (then it is a point under the topic above).
 * A leading `1.` / `1)` on a topic is optional: {@link textToKeyPoints} numbers
 * the topics itself, the way the backend does, and only uses a typed number to
 * move the topic when the user changed it.
 */
export interface KeyPointLine {
  kind: 'topic' | 'point';
  text: string;
  /** 1-based line in the edited text, for error messages. */
  line: number;
  /** The number typed in front of a topic, if any. */
  number?: number;
}

// `*` and `-` only count as bullets when followed by whitespace, so `**bold**`
// and `-5 degrees` stay topics.
const POINT_LINE = /^[-*•](?:\s+(.*))?$/;
// Requires whitespace after the number, so "3.5 million budget" keeps its "3.".
const TOPIC_NUMBER = /^(\d+)[.)](?:\s+|$)/;

/** Splits the edited text into topic and point lines, skipping blank ones. */
export const parseKeyPointsText = (text: string): KeyPointLine[] => {
  const lines: KeyPointLine[] = [];

  text.split(/\r?\n/).forEach((raw, index) => {
    const trimmed = raw.trim();
    if (!trimmed) return;

    const point = trimmed.match(POINT_LINE);
    if (point) {
      const pointText = point[1]?.trim();
      if (pointText) lines.push({ kind: 'point', text: pointText, line: index + 1 });
      return;
    }

    const topic = trimmed.replace(TOPIC_NUMBER, '').trim();
    if (!topic) return;
    const typedNumber = trimmed.match(TOPIC_NUMBER)?.[1];
    lines.push({
      kind: 'topic',
      text: topic,
      line: index + 1,
      ...(typedNumber !== undefined && { number: Number(typedNumber) }),
    });
  });

  return lines;
};

/**
 * Turns the backend's `key_discussion_points_text` into the text the user edits:
 * numbered topics, `- ` points, and a blank line between topics. A topic with no
 * points is a bare line.
 */
export const keyPointsToText = (markdown: string | undefined | null): string =>
  extractDiscussionStructure(markdown)
    .map(({ header, points }, index) =>
      [
        `${index + 1}. ${header.replace(TOPIC_NUMBER, '')}`,
        ...points.map((point) => `- ${point}`),
      ].join('\n'),
    )
    .join('\n\n');

interface KeyPointTopic {
  heading: string;
  points: string[];
  number?: number;
}

const groupTopics = (text: string): KeyPointTopic[] => {
  const topics: KeyPointTopic[] = [];

  parseKeyPointsText(text).forEach(({ kind, text: lineText, number }) => {
    if (kind === 'topic') topics.push({ heading: lineText, points: [], number });
    else topics[topics.length - 1]?.points.push(lineText);
  });

  return topics;
};

/**
 * Puts the topics in the order the user asked for. A topic keeps its place in
 * the text unless the user changed its number from the one it had when the
 * editor opened: then the number is the position it should end up in.
 *
 * A moved block that kept its old number (cut and paste) is not a request,
 * and neither is a topic with no number. Topics are matched to the opened text
 * by their wording, so a topic that was both moved and reworded counts as new.
 */
const orderTopics = (topics: KeyPointTopic[], savedText: string): KeyPointTopic[] => {
  const unmatched = groupTopics(savedText);
  const anchors: KeyPointTopic[] = [];
  const requested: Array<KeyPointTopic & { number: number }> = [];

  topics.forEach((topic) => {
    const twinIndex = unmatched.findIndex(({ heading }) => heading === topic.heading);
    const [twin] = twinIndex >= 0 ? unmatched.splice(twinIndex, 1) : [];

    if (topic.number === undefined || topic.number === twin?.number) anchors.push(topic);
    else requested.push({ ...topic, number: topic.number });
  });

  // Ascending, so each insert lands where its number says. The sort is stable,
  // so equal numbers keep their order in the text.
  requested.sort((a, b) => a.number - b.number);

  const ordered = [...anchors];
  let previous: { number: number; index: number } | null = null;
  requested.forEach((topic) => {
    const index =
      previous?.number === topic.number
        ? previous.index + 1
        : Math.min(Math.max(topic.number - 1, 0), ordered.length);
    ordered.splice(index, 0, topic);
    previous = { number: topic.number, index };
  });

  return ordered;
};

/**
 * Writes the edited text in the `### N. Topic` / `* point` format the backend
 * accepts, with the topics renumbered 1..n. `savedText` is what the editor
 * opened with, so that only numbers the user changed reorder topics. Call
 * {@link findPointBeforeTopic} first: a point with no topic above it has
 * nowhere to go and is dropped here.
 */
export const textToKeyPoints = (text: string, savedText = ''): string =>
  orderTopics(groupTopics(text), savedText)
    .map(({ heading, points }, index) =>
      [
        `### ${index + 1}. ${heading}`,
        ...(points.length > 0 ? [points.map((point) => `* ${point}`).join('\n')] : []),
      ].join('\n\n'),
    )
    .join('\n\n');

/**
 * The line of a point written before any topic, which the backend rejects, or
 * `null` when there is none. Only the first line can be such a point, since any
 * topic after it gives later points a home.
 */
export const findPointBeforeTopic = (text: string): number | null => {
  const [first] = parseKeyPointsText(text);
  return first?.kind === 'point' ? first.line : null;
};
